import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import AuthImg from "@/components/AuthImg";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import {
  Loader2, User2, Mail, Activity, Wifi, WifiOff, Clock, AlertTriangle, Trash2,
  KeyRound, ChevronRight, MessageSquare, Megaphone, Search, ChevronLeft, Sparkles, FileDown,
  LockOpen, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { UserMessageDialog } from "@/components/admin/UserMessageDialog";
import { UserDetailDialog } from "@/components/admin/UserDetailDialog";
import { TeamPerformanceContent } from "@/components/admin/TeamPerformance";
import { UsersRound } from "lucide-react";

const ROLES = [
  { v: "admin", l: "Admin" },
  { v: "gutachter", l: "Gutachter" },
  { v: "backoffice", l: "Backoffice" },
  { v: "mitarbeiter", l: "Mitarbeiter" },
  { v: "franchise", l: "Franchise" },
  { v: "provision", l: "Provisionspartner" },
];
const STATUSES = [
  { v: "active", l: "Aktiv" },
  { v: "inactive", l: "Inaktiv" },
  { v: "suspended", l: "Gesperrt" },
  { v: "deactivated", l: "Deaktiviert" },
];

const REFRESH_MS = 15_000;

const TABS = [
  { key: "uebersicht", label: "Benutzer-Übersicht", icon: User2, desc: "Live-Übersicht aller Konten · automatische Aktualisierung alle 15 s" },
  { key: "team", label: "Team-Leistung", icon: UsersRound, desc: "Akten-Counter pro Mitarbeiter" },
];

function formatRelative(iso) {
  if (!iso) return "noch nie";
  try {
    const ts = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - ts);
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return "gerade eben";
    const min = Math.floor(sec / 60);
    if (min < 60) return `vor ${min} Min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `vor ${h} Std`;
    const d = Math.floor(h / 24);
    return `vor ${d} ${d === 1 ? "Tag" : "Tagen"}`;
  } catch (_e) {
    return "—";
  }
}

function fmtCancelDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return iso; }
}

function SubBadge({ subStatus, plan, trialDaysLeft, cancellationEffectiveAt }) {
  // Offene Kündigung mit 3-Monats-Frist (User noch aktiv, läuft aus)
  if (cancellationEffectiveAt && subStatus === "active") {
    return (
      <Badge
        variant="outline"
        className="bg-amber-50 text-amber-800 border-amber-200"
        title={`Gekündigt — Zugang endet am ${fmtCancelDate(cancellationEffectiveAt)}`}
      >
        Gekündigt zum {fmtCancelDate(cancellationEffectiveAt)}
      </Badge>
    );
  }
  if (subStatus === "active") {
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{plan || "Pro"} · aktiv</Badge>;
  }
  if (subStatus === "trialing") {
    const tone = (trialDaysLeft ?? 5) <= 1 ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200";
    return <Badge variant="outline" className={tone}>Trial · {trialDaysLeft ?? "—"}T übrig</Badge>;
  }
  if (subStatus === "trial_expired") {
    return <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">Trial abgelaufen</Badge>;
  }
  if (subStatus === "past_due") {
    return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">Zahlung offen</Badge>;
  }
  if (subStatus === "cancelled" || subStatus === "canceled") {
    return <Badge variant="outline" className="bg-zinc-200 text-zinc-700 border-zinc-300">Gekündigt · gesperrt</Badge>;
  }
  return <Badge variant="outline" className="bg-zinc-100 text-zinc-600">–</Badge>;
}

function WorkTypeBadge({ user, suffix = "" }) {
  const isProvision = user.role === "provision" || user.work_type === "provision";
  if (isProvision) {
    return (
      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`user-worktype-${suffix}${user.email}`}>
        Provisionsbasis · 200 €/Auftrag
      </Badge>
    );
  }
  if (user.role === "gutachter") {
    return (
      <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200" data-testid={`user-worktype-${suffix}${user.email}`}>
        Selbstständig
      </Badge>
    );
  }
  return null;
}

const GDV_META = {
  pending:   { label: "GDV offen",       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  requested: { label: "GDV beantragt",   cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  delivered: { label: "GDV versendet",   cls: "bg-violet-50 text-violet-700 border-violet-200" },
  completed: { label: "GDV aktiv",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "GDV abgebrochen", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
};

function GdvBadge({ status, email }) {
  const meta = GDV_META[status];
  if (!meta) return null;
  return (
    <span
      title={`GDV: ${meta.label}`}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider",
        meta.cls,
      )}
      data-testid={`user-gdv-badge-${email}`}
    >
      <KeyRound className="h-2.5 w-2.5" /> {meta.label}
    </span>
  );
}

function OnlineDot({ online }) {
  return (
    <span className="relative inline-flex items-center justify-center" data-testid={online ? "online-dot" : "offline-dot"}>
      {online && (
        <span className="absolute inline-flex h-3 w-3 animate-ping rounded-full bg-emerald-400 opacity-60"></span>
      )}
      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-zinc-300"}`}></span>
    </span>
  );
}

export default function UsersAdmin() {
  const [tab, setTab] = useState("uebersicht");
  const activeMeta = TABS.find((t) => t.key === tab) || TABS[0];
  return (
    <Layout
      title="Benutzer"
      subtitle={activeMeta.desc}
    >
      {/* Tab-Umschalter: Übersicht · Team-Leistung */}
      <div className="flex gap-2 mb-5" data-testid="users-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
              tab === key
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
            )}
            data-testid={`users-tab-btn-${key}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>
      {tab === "uebersicht" ? (
        <div data-testid="users-tab-uebersicht">
          <UsersOverview />
        </div>
      ) : (
        <div data-testid="users-tab-team">
          <TeamPerformanceContent />
        </div>
      )}
    </Layout>
  );
}

function UsersOverview() {
  const { user: me } = useAuth();
  const currentUserId = me?.id;
  const [users, setUsers] = useState([]);
  const [presence, setPresence] = useState({});
  const [gdvMap, setGdvMap] = useState({});
  const [onlineCount, setOnlineCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | online | trial | active_sub
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const NEW_USER_THRESHOLD_MS = 48 * 60 * 60 * 1000; // 48h
  const [msgTarget, setMsgTarget] = useState(null); // user-Objekt | "all" | null
  const [detailUser, setDetailUser] = useState(null); // Popup mit allen Registrierungs-Infos
  const ts = useRef(0);

  const load = async () => {
    try {
      const [{ data: u }, { data: p }] = await Promise.all([
        api.get("/users"),
        api.get("/users/admin/presence"),
      ]);
      setUsers(u.users || []);
      const map = {};
      (p.users || []).forEach((x) => { map[x.id] = x; });
      setPresence(map);
      setOnlineCount(p.online_count || 0);
      ts.current = Date.now();
    } catch (e) {
      // silent on refresh failures (initial load already showed the toast)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, REFRESH_MS);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GDV-Antrag-Status pro User (1× laden — kein 15s-Polling, Endpoint macht Backfill)
  useEffect(() => {
    api.get("/admin/gdv-requests")
      .then(({ data }) => {
        const m = {};
        (data.requests || []).forEach((r) => { if (!m[r.user_id]) m[r.user_id] = { status: r.status, id: r.id }; });
        setGdvMap(m);
      })
      .catch(() => {});
  }, []);

  const update = async (id, changes) => {
    try {
      const { data } = await api.patch(`/users/${id}`, changes);
      setUsers((u) => u.map((x) => (x.id === id ? data.user : x)));
      if (data.notice) {
        toast.success("Benutzer aktualisiert", { description: data.notice, duration: 8000 });
      } else {
        toast.success("Benutzer aktualisiert");
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const unlockLogin = async (user) => {
    try {
      const { data } = await api.post("/auth/unlock", { email: user.email });
      if (data.was_locked) {
        toast.success("Login-Sperre aufgehoben", { description: user.email });
      } else {
        toast.info("Keine aktive Sperre für dieses Konto", { description: user.email });
      }
    } catch (e) {
      toast.error(apiError(e, "Sperre konnte nicht aufgehoben werden"));
    }
  };

  const sendGdvAntrag = async (user) => {
    if (!window.confirm(
      `GDV-Antrag für "${user.name}" jetzt an kundenbetreuung@gdv-dl.de senden?\n\n`
      + "Der Antrag wird als PDF-Anhang generiert und automatisch versendet."
    )) return;
    try {
      const { data } = await api.post(`/admin/mail/gdv-antrag/${user.id}`);
      if (data.delivered) {
        toast.success("GDV-Antrag versendet", {
          description: `An ${data.to} (${data.mode.toUpperCase()})`,
        });
      } else {
        toast.warning("Nur im Dry-Run gespeichert — SMTP/Resend nicht erreichbar");
      }
    } catch (e) {
      toast.error(apiError(e, "GDV-Antrag konnte nicht versendet werden"));
    }
  };


  const [gdvPdfBusy, setGdvPdfBusy] = useState(null); // user.id während Download
  const downloadGdvPdf = async (user) => {
    const req = gdvMap[user.id];
    if (!req?.id) {
      toast.info("Für diesen Benutzer existiert noch kein GDV-Antrag.");
      return;
    }
    setGdvPdfBusy(user.id);
    try {
      const { data } = await api.get(`/admin/gdv-requests/${req.id}/letter`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GDV-Antrag-${(user.name || user.email).replace(/[\s/]+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(apiError(e, "GDV-Antrag-PDF konnte nicht geladen werden"));
    } finally {
      setGdvPdfBusy(null);
    }
  };

  const removeUser = async (user) => {
    const confirmText = `Benutzer "${user.name || user.email}" wirklich löschen?\n\n` +
      `Die Aktion kann NICHT rückgängig gemacht werden. Vorhandene Akten ` +
      `bleiben erhalten, sind aber dann keinem Bearbeiter mehr zugewiesen.`;
    if (!window.confirm(confirmText)) return;
    try {
      const { data } = await api.delete(`/users/${user.id}`);
      setUsers((u) => u.filter((x) => x.id !== user.id));
      const note = data.orphaned_cases > 0
        ? ` (${data.orphaned_cases} Akten ohne Zuweisung)`
        : "";
      toast.success(`Benutzer "${user.email}" gelöscht${note}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // „Neu" = registriert in den letzten 48h → blau hervorheben + ganz oben
  const isNewUser = (u) => {
    const t = u?.created_at ? new Date(u.created_at).getTime() : 0;
    return t > 0 && Date.now() - t < NEW_USER_THRESHOLD_MS;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result = users.filter((u) => {
      const p = presence[u.id] || {};
      // KPI-Filter
      if (filter === "online" && !p.is_online) return false;
      if (filter === "trial" && p.subscription_status !== "trialing" && p.subscription_status !== "trial_expired") return false;
      if (filter === "active_sub" && p.subscription_status !== "active") return false;
      // Rollen-Filter
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      // Status-Filter
      if (statusFilter !== "all" && u.status !== statusFilter) return false;
      // Such-Filter — über Name, E-Mail, Firma, Telefon, Stadt
      if (q) {
        const hay = [u.name, u.email, u.company, u.phone, u.mobile, u.city, u.postal_code]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Sortierung: neue Nutzer (≤48h) zuerst, dann Online, dann nach created_at desc
    result.sort((a, b) => {
      const aNew = isNewUser(a) ? 1 : 0;
      const bNew = isNewUser(b) ? 1 : 0;
      if (aNew !== bNew) return bNew - aNew;
      const aOn = (presence[a.id]?.is_online) ? 1 : 0;
      const bOn = (presence[b.id]?.is_online) ? 1 : 0;
      if (aOn !== bOn) return bOn - aOn;
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
    return result;
  }, [users, presence, filter, search, roleFilter, statusFilter]);

  // Reset Seite bei jeder Filter-/Such-Änderung
  useEffect(() => { setPage(0); }, [filter, search, roleFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paged = useMemo(
    () => filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [filtered, currentPage]
  );

  const trialCount = useMemo(
    () => users.filter((u) => (presence[u.id]?.subscription_status === "trialing")).length,
    [users, presence]
  );
  const activeSubCount = useMemo(
    () => users.filter((u) => (presence[u.id]?.subscription_status === "active")).length,
    [users, presence]
  );

  return (
    <>
      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi
          testid="kpi-online"
          icon={<Activity className="h-4 w-4" />}
          label="Aktuell online"
          value={onlineCount}
          tone="emerald"
          live
          onClick={() => setFilter(filter === "online" ? "all" : "online")}
          active={filter === "online"}
        />
        <Kpi
          testid="kpi-total"
          icon={<User2 className="h-4 w-4" />}
          label="Konten gesamt"
          value={users.length}
          tone="zinc"
          onClick={() => setFilter("all")}
          active={filter === "all"}
        />
        <Kpi
          testid="kpi-trial"
          icon={<Clock className="h-4 w-4" />}
          label="In Testphase"
          value={trialCount}
          tone="amber"
          onClick={() => setFilter(filter === "trial" ? "all" : "trial")}
          active={filter === "trial"}
        />
        <Kpi
          testid="kpi-active-sub"
          icon={<Wifi className="h-4 w-4" />}
          label="Mit aktivem Abo"
          value={activeSubCount}
          tone="cyan"
          onClick={() => setFilter(filter === "active_sub" ? "all" : "active_sub")}
          active={filter === "active_sub"}
        />
      </div>

      {/* Toolbar: Suche, Filter, Broadcast */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="users-filter-toolbar">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche: Name, E-Mail, Firma, Telefon, Ort …"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition"
            data-testid="users-search-input"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="users-role-filter">
            <SelectValue placeholder="Alle Rollen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Rollen</SelectItem>
            {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[150px]" data-testid="users-status-filter">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-zinc-500 ml-1" data-testid="users-result-count">
          {filtered.length} Treffer
        </div>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setMsgTarget("all")}
            className="inline-flex items-center gap-2 px-3.5 h-9 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold transition"
            data-testid="message-all-users-btn"
          >
            <Megaphone className="h-3.5 w-3.5" /> Nachricht an alle
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500" data-testid="users-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
        </div>
      ) : (
        <>
        <div className="hidden xl:block rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="users-table">
          <div className="grid grid-cols-12 px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-bold text-zinc-500 border-b border-zinc-200 bg-zinc-50">
            <div className="col-span-1">Live</div>
            <div className="col-span-3">Benutzer</div>
            <div className="col-span-2">Rolle</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Abo · GDV</div>
            <div className="col-span-1 text-right">Aktiv.</div>
            <div className="col-span-1 text-right">Aktion</div>
          </div>
          {filtered.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-zinc-500" data-testid="users-empty">Keine Treffer</div>
          )}
          {paged.map((u) => {
            const p = presence[u.id] || {};
            const online = !!p.is_online;
            const isNew = isNewUser(u);
            return (
              <div
                key={u.id}
                className={cn(
                  "grid grid-cols-12 px-6 py-4 items-center border-b border-zinc-200 last:border-0 transition-colors",
                  isNew ? "bg-sky-50/70 hover:bg-sky-100/60" : "hover:bg-zinc-50/40",
                )}
                data-testid={`user-row-${u.email}`}
              >
                <div className="col-span-1 flex items-center gap-2" data-testid={`user-presence-${u.email}`}>
                  <OnlineDot online={online} />
                  <span className={`text-[11px] uppercase tracking-wider ${online ? "text-emerald-700 font-semibold" : "text-zinc-400"}`}>
                    {online ? "Online" : "Offline"}
                  </span>
                </div>
                <div className="col-span-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => setDetailUser(u)}
                    className="flex items-center gap-3 text-left w-full rounded-lg -mx-1 px-1 py-0.5 hover:bg-white/70 transition-colors"
                    title="Alle Informationen anzeigen"
                    data-testid={`user-open-detail-${u.email}`}
                  >
                    <div className="relative h-9 w-9 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-600 shrink-0">
                      {u.avatar_path ? (
                        <AuthImg path={u.avatar_path} className="h-full w-full object-cover" alt={u.name} />
                      ) : (
                        u.name?.slice(0, 2).toUpperCase() || <User2 className="h-4 w-4" />
                      )}
                      {online && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white"></span>}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {u.name}
                        {isNew && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky-600 text-white px-1.5 py-0.5 rounded" data-testid={`user-new-badge-${u.email}`}>
                            <Sparkles className="h-2.5 w-2.5" /> Neu
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3" />{u.email}
                      </div>
                    </div>
                  </button>
                </div>
                <div className="col-span-2">
                  <Select value={u.role} onValueChange={(v) => update(u.id, { role: v, status: u.status })}>
                    <SelectTrigger className="w-full max-w-[140px] h-8" data-testid={`user-role-select-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="mt-1"><WorkTypeBadge user={u} /></div>
                </div>
                <div className="col-span-2">
                  <Select value={u.status} onValueChange={(v) => update(u.id, { role: u.role, status: v })}>
                    <SelectTrigger className="w-full max-w-[140px] h-8" data-testid={`user-status-select-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                  </Select>
                  {u.status === "deactivated" && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-rose-600">
                      <AlertTriangle className="h-3 w-3" /> Trial abgelaufen
                    </div>
                  )}
                </div>
                <div className="col-span-2 flex flex-col items-start gap-1">
                  <SubBadge
                    subStatus={p.subscription_status || u.subscription_status}
                    plan={p.subscription_plan || u.subscription_plan}
                    trialDaysLeft={p.trial_days_remaining}
                    cancellationEffectiveAt={u.cancellation_effective_at}
                  />
                  <GdvBadge status={gdvMap[u.id]?.status} email={u.email} />
                </div>
                <div className="col-span-1 text-right text-[11px] text-zinc-500" data-testid={`user-last-seen-${u.email}`}>
                  {p.last_seen_at ? formatRelative(p.last_seen_at) : <span className="inline-flex items-center gap-1"><WifiOff className="h-3 w-3" /></span>}
                </div>
                <div className="col-span-1 flex justify-end gap-0.5" data-testid={`user-actions-${u.email}`}>
                  <button
                    type="button"
                    onClick={() => setDetailUser(u)}
                    title="Alle Informationen anzeigen"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                    data-testid={`user-detail-btn-${u.email}`}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {gdvMap[u.id]?.id && (
                    <button
                      type="button"
                      onClick={() => downloadGdvPdf(u)}
                      disabled={gdvPdfBusy === u.id}
                      title="GDV-Antrag als PDF herunterladen"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors disabled:opacity-50 disabled:cursor-wait"
                      data-testid={`user-gdv-pdf-btn-${u.email}`}
                    >
                      {gdvPdfBusy === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => sendGdvAntrag(u)}
                    title="GDV-Antrag jetzt an kundenbetreuung@gdv-dl.de senden"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                    data-testid={`user-gdv-send-btn-${u.email}`}
                  >
                    <KeyRound className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => unlockLogin(u)}
                    title="Login-Sperre aufheben (zu viele Fehlversuche)"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                    data-testid={`user-unlock-btn-${u.email}`}
                  >
                    <LockOpen className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMsgTarget(u)}
                    title="Nachricht senden"
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                    data-testid={`user-message-btn-${u.email}`}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeUser(u)}
                    disabled={u.id === currentUserId}
                    title={u.id === currentUserId ? "Eigenes Konto kann nicht gelöscht werden" : "Benutzer löschen"}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`user-delete-btn-${u.email}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile/Tablet: Card-Liste */}
        <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3" data-testid="users-cards-mobile">
          {filtered.length === 0 ? (
            <div className="md:col-span-2 lg:col-span-1 rounded-2xl border border-zinc-200 bg-white px-4 py-8 text-center text-sm text-zinc-500">Keine Treffer</div>
          ) : paged.map((u) => {
            const p = presence[u.id] || {};
            const online = !!p.is_online;
            const isNew = isNewUser(u);
            return (
              <div key={u.id} className={cn("rounded-2xl border bg-white p-4", isNew ? "border-sky-300 bg-sky-50/60" : "border-zinc-200")} data-testid={`user-card-${u.email}`}>
                <div className="flex items-start gap-3">
                  <div className="relative h-10 w-10 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-600 shrink-0">
                    {u.avatar_path ? (
                      <AuthImg path={u.avatar_path} className="h-full w-full object-cover" alt={u.name} />
                    ) : (
                      u.name?.slice(0, 2).toUpperCase() || <User2 className="h-4 w-4" />
                    )}
                    {online && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white"></span>}
                  </div>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setDetailUser(u)} data-testid={`user-open-detail-m-${u.email}`}>
                    <div className="text-sm font-semibold text-zinc-900 truncate flex items-center gap-1.5">
                      {u.name}
                      {isNew && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky-600 text-white px-1.5 py-0.5 rounded">
                          <Sparkles className="h-2.5 w-2.5" /> Neu
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                    <div className="flex items-center gap-1.5 mt-1 text-[10px] uppercase tracking-wider">
                      <OnlineDot online={online} />
                      <span className={online ? "text-emerald-700 font-semibold" : "text-zinc-400"}>
                        {online ? "Online" : "Offline"}
                      </span>
                      {p.last_seen_at && <span className="text-zinc-400 normal-case tracking-normal">· {formatRelative(p.last_seen_at)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setDetailUser(u)}
                      title="Alle Informationen anzeigen"
                      className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                      data-testid={`user-detail-btn-m-${u.email}`}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {gdvMap[u.id]?.id && (
                      <button
                        type="button"
                        onClick={() => downloadGdvPdf(u)}
                        disabled={gdvPdfBusy === u.id}
                        title="GDV-Antrag als PDF herunterladen"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors disabled:opacity-50"
                        data-testid={`user-gdv-pdf-btn-m-${u.email}`}
                      >
                        {gdvPdfBusy === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setMsgTarget(u)}
                      title="Nachricht senden"
                      className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
                      data-testid={`user-message-btn-m-${u.email}`}
                    >
                      <MessageSquare className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeUser(u)}
                      disabled={u.id === currentUserId}
                      title={u.id === currentUserId ? "Eigenes Konto kann nicht gelöscht werden" : "Benutzer löschen"}
                      className="inline-flex items-center justify-center h-9 w-9 rounded-md text-zinc-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-30"
                      data-testid={`user-delete-btn-m-${u.email}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Select value={u.role} onValueChange={(v) => update(u.id, { role: v, status: u.status })}>
                    <SelectTrigger className="w-full h-9" data-testid={`user-role-select-m-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={u.status} onValueChange={(v) => update(u.id, { role: u.role, status: v })}>
                    <SelectTrigger className="w-full h-9" data-testid={`user-status-select-m-${u.email}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-3">
                  <WorkTypeBadge user={u} suffix="m-" />
                  <SubBadge
                    subStatus={p.subscription_status || u.subscription_status}
                    plan={p.subscription_plan || u.subscription_plan}
                    trialDaysLeft={p.trial_days_remaining}
                    cancellationEffectiveAt={u.cancellation_effective_at}
                  />
                  <GdvBadge status={gdvMap[u.id]?.status} email={`m-${u.email}`} />
                  {u.status === "deactivated" && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-rose-600">
                      <AlertTriangle className="h-3 w-3" /> Trial abgelaufen
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="flex items-center justify-between mt-4 px-2" data-testid="users-pagination">
            <div className="text-xs text-zinc-500">
              Seite <span className="font-semibold text-zinc-900">{currentPage + 1}</span> von <span className="font-semibold text-zinc-900">{pageCount}</span>
              <span className="text-zinc-400"> · {filtered.length} Treffer</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                data-testid="users-page-prev"
                aria-label="Vorherige Seite"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {/* Page-Number-Buttons (max 7 sichtbar) */}
              {Array.from({ length: pageCount }).map((_, i) => {
                // Kompaktes Sliding-Window: aktuelle Seite ± 2
                if (pageCount > 7) {
                  const showFirst = i === 0;
                  const showLast = i === pageCount - 1;
                  const inWindow = Math.abs(i - currentPage) <= 1;
                  if (!showFirst && !showLast && !inWindow) {
                    // Show ellipsis once on each side
                    if (i === 1 && currentPage > 3) return <span key={`e-${i}`} className="px-1 text-zinc-300">…</span>;
                    if (i === pageCount - 2 && currentPage < pageCount - 4) return <span key={`e-${i}`} className="px-1 text-zinc-300">…</span>;
                    return null;
                  }
                }
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i)}
                    className={cn(
                      "inline-flex items-center justify-center h-8 min-w-[32px] px-2 rounded-md border text-xs font-semibold transition",
                      i === currentPage
                        ? "border-sky-600 bg-sky-600 text-white"
                        : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
                    )}
                    data-testid={`users-page-${i + 1}`}
                  >
                    {i + 1}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setPage(Math.min(pageCount - 1, currentPage + 1))}
                disabled={currentPage === pageCount - 1}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                data-testid="users-page-next"
                aria-label="Nächste Seite"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        </>
      )}

      <UserMessageDialog
        open={!!msgTarget}
        user={msgTarget === "all" ? null : msgTarget}
        onOpenChange={(o) => { if (!o) setMsgTarget(null); }}
      />
      <UserDetailDialog
        user={detailUser}
        presence={detailUser ? presence[detailUser.id] : null}
        isNew={detailUser ? isNewUser(detailUser) : false}
        onOpenChange={() => setDetailUser(null)}
      />
    </>
  );
}

function Kpi({ icon, label, value, tone = "zinc", live, onClick, active, testid }) {
  const tones = {
    emerald: "from-emerald-50 to-cyan-50 text-emerald-700 ring-emerald-200",
    amber: "from-amber-50 to-orange-50 text-amber-700 ring-amber-200",
    cyan: "from-cyan-50 to-sky-50 text-cyan-700 ring-cyan-200",
    zinc: "from-zinc-50 to-zinc-100 text-zinc-700 ring-zinc-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-2xl bg-gradient-to-br ${tones[tone] || tones.zinc} ring-1 px-4 py-4 transition hover:shadow-md hover:-translate-y-0.5 ${active ? "ring-2 ring-offset-2 ring-zinc-900/10" : ""}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold opacity-80">
        {icon}{label}
        {live && (
          <span className="ml-auto inline-flex items-center gap-1 text-emerald-600">
            <span className="relative inline-flex">
              <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-emerald-400 opacity-70"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            <span className="text-[10px] uppercase tracking-wider">Live</span>
          </span>
        )}
      </div>
      <div className="mt-2 text-3xl font-bold text-zinc-900">{value}</div>
    </button>
  );
}
